import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Skill } from "../types";

interface SkillsShowcaseProps {
  skills: Skill[];
}

const SkillsShowcase = ({ skills }: SkillsShowcaseProps) => {
  const categories = Array.from(new Set(skills.map((skill) => skill.category)));

  return (
    <section className="py-20 bg-surface/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary/20 rounded-full mb-4">
            <Icon name="Code2" size={18} color="var(--color-secondary)" />
            <span className="text-sm font-mono text-secondary">Technical Expertise</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            Skills &amp; Technologies
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Continuously expanding my technical toolkit through academic coursework and hands-on projects
          </p>
        </div>

        <div className="space-y-12">
          {categories.map((category, categoryIndex) => (
            <div key={category} className="animate-fade-in-up" style={{ animationDelay: `${categoryIndex * 0.2}s` }}>
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <div className="w-1 h-8 bg-primary rounded-full"></div>
                {category}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {skills
                  .filter((skill) => skill.category === category)
                  .map((skill, index) => (
                    <div
                      key={skill.id}
                      className="group bg-surface border border-border rounded-xl p-6 hover:border-primary/50 transition-all duration-300 hover:shadow-elevation-2"
                      style={{ animationDelay: `${(categoryIndex * 0.2) + (index * 0.1)}s` }}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                          <Icon name={skill.icon} size={24} color="var(--color-primary)" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-foreground">{skill.name}</h4>
                          <p className="text-sm text-muted-foreground">{skill.proficiency}% Proficiency</p>
                        </div>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${skill.proficiency}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/skills">
            <Button variant="outline" size="lg" iconName="ArrowRight" iconPosition="right">
              Explore All Skills
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SkillsShowcase;